import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type { IRenewalPolicy, TRenewalTimeRange } from '@/@types/aiReply';

interface IRenewalWidgetProps {
  policies: IRenewalPolicy[] | undefined;
  timeRange: TRenewalTimeRange;
  onTimeRangeChange: (value: TRenewalTimeRange) => void;
}

function getUrgencyVariant(daysUntilExpiry: number): 'destructive' | 'default' | 'secondary' {
  if (daysUntilExpiry <= 14) return 'destructive';
  if (daysUntilExpiry <= 30) return 'default';
  return 'secondary';
}

function formatDate(timestampMs: number): string {
  return new Date(timestampMs).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function RenewalWidget({ policies, timeRange, onTimeRangeChange }: IRenewalWidgetProps) {
  const navigate = useNavigate();
  const isLoading = policies === undefined;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-base font-semibold">Upcoming Renewals</CardTitle>
        <Select
          value={timeRange}
          onValueChange={v => onTimeRangeChange(v as TRenewalTimeRange)}
        >
          <SelectTrigger className="w-28 h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="30d">30 days</SelectItem>
            <SelectItem value="60d">60 days</SelectItem>
            <SelectItem value="90d">90 days</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : policies.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No policies expiring within {timeRange === '30d' ? '30' : timeRange === '60d' ? '60' : '90'} days
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Policy #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Expiry</TableHead>
                <TableHead>Days Left</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {policies.map(policy => (
                <TableRow
                  key={policy.policyId}
                  className={cn('cursor-pointer hover:bg-muted/50')}
                  onClick={() => navigate(`/insurance/policies/${policy.policyId}`)}
                >
                  <TableCell className="font-mono text-sm">{policy.policyNumber}</TableCell>
                  <TableCell>{policy.customerName || '—'}</TableCell>
                  <TableCell>{policy.insuranceType}</TableCell>
                  <TableCell>{formatDate(policy.expiryDate)}</TableCell>
                  <TableCell>
                    <Badge variant={getUrgencyVariant(policy.daysUntilExpiry)}>
                      {policy.daysUntilExpiry}d
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
